import type { Ref } from 'react'
import type { TextBoxColors } from 'atoms/TextBox'
import type { INavGroup } from './NavGroup'

import { styled } from '@linaria/react'
import { IconSC } from 'atoms/Icon'
import { NavDirection, NavTabLink } from 'atoms/NavTab'
import { TextBoxSC } from 'atoms/TextBox'
import {
  DropdownBody,
  DropdownHeaderStyled,
  DropdownStyled,
} from 'blocks/Dropdown'
import { DropdownItemTypeButtonStyled } from 'blocks/Dropdown/DropdownItems/DropdownItemTypeButton'
import { FIXED_INDENTS, INDENTS, MEDIA } from 'vars/index'
import { NavAlignItems, NavBackground, NavJustifyContent } from './NavGroup.h'

export const NavGroupMore = styled.div``

export const NavGroupSC = styled.ul<
  Omit<INavGroup, 'ref' | 'index'> & {
    ref?: Ref<HTMLUListElement>
  }
>`
  display: flex;
  width: ${({ direction }) =>
    direction === NavDirection.COLUMN ? 'max-content' : 'inherit'};
  flex-direction: ${({ direction }) => direction || NavDirection.ROW};
  gap: ${({ direction, gapColumn, gapRow }) =>
    direction === NavDirection.ROW ? `${gapColumn}` : `${gapRow}`};
  align-items: ${({ direction, alignItems }) =>
    direction === NavDirection.COLUMN && !alignItems
      ? NavAlignItems.FlexStart
      : (alignItems ?? NavAlignItems.Center)};
  justify-content: ${({ direction, justifyContent }) =>
    direction === NavDirection.COLUMN && !justifyContent
      ? NavJustifyContent.FlexStart
      : (justifyContent ?? NavJustifyContent.Center)};
`
export const NavGroupWithMoreSC = styled.div<
  Omit<INavGroup, 'index' | 'children' | 'color'> & {
    colorDefault?: TextBoxColors
    colorMore?: TextBoxColors
    maxWidth?: number
  }
>`
  --alignItemsDropdown: -10px;
  --ariaDropdown: 10px;
  display: flex;
  flex: 1 0;
  gap: ${({ direction, gapColumn, gapRow }) =>
    direction === NavDirection.ROW ? `${gapColumn}` : `${gapRow}`};
  min-width: ${({ direction }) =>
    direction === NavDirection.COLUMN ? `auto` : 'inherit'};
  width: ${({ direction }) =>
    direction === NavDirection.COLUMN ? 'max-content' : 'inherit'};
  height: inherit;
  align-items: ${({ direction, alignItems }) =>
    direction === NavDirection.COLUMN
      ? NavAlignItems.FlexStart
      : alignItems
        ? alignItems
        : NavAlignItems.Center};
  justify-content: ${({ direction, justifyContent }) =>
    direction === NavDirection.COLUMN
      ? NavJustifyContent.FlexStart
      : justifyContent
        ? justifyContent
        : NavJustifyContent.Center};
  flex-direction: ${({ direction }) => direction || NavDirection.ROW};
  color: ${({ colorDefault }) => `var(${colorDefault})`};

  & ${DropdownStyled} {
    position: relative;
    display: ${({ direction }) =>
      direction === NavDirection.ROW ? 'inline-block' : 'flex'};
    align-items: ${({ direction, alignItems }) =>
      direction === NavDirection.COLUMN
        ? NavAlignItems.FlexStart
        : alignItems
          ? alignItems
          : NavAlignItems.Center};
    justify-content: ${({ direction, justifyContent }) =>
      direction === NavDirection.COLUMN
        ? NavJustifyContent.FlexStart
        : justifyContent
          ? justifyContent
          : NavJustifyContent.Center};
    padding: var(${FIXED_INDENTS.INDENT_40});

    ${MEDIA.NO_TOUCH} {
      &:hover {
        background: ${({ backgroundMoreTitleHovered, direction }) =>
          direction === NavDirection.COLUMN
            ? `var(${backgroundMoreTitleHovered})`
            : `var${NavBackground.TRANSPARENT}`};

        ${DropdownHeaderStyled} {
          color: ${({ colorMoreTitleHovered }) =>
            `var(${colorMoreTitleHovered})`};
        }
      }
    }

    & ${DropdownHeaderStyled} {
      color: ${({ colorDefault }) => `var(${colorDefault})`};
      align-items: ${({ direction, alignItems }) =>
        direction === NavDirection.COLUMN
          ? NavAlignItems.Center
          : alignItems
            ? alignItems
            : NavAlignItems.Center};
      justify-content: ${({ direction, justifyContent }) =>
        direction === NavDirection.COLUMN
          ? NavJustifyContent.FlexStart
          : justifyContent
            ? justifyContent
            : NavJustifyContent.Center};

      & ${IconSC} {
        margin-right: var(${INDENTS.INDENT_35});
        transform: none;

        &:only-child {
          margin: 0;
        }
      }

      & ${TextBoxSC} {
        color: ${({ colorMore }) => `var(${colorMore})`};
        min-width: ${({ width, direction }) =>
          direction === NavDirection.COLUMN ? `${width}px` : 'auto'};
        text-align: start;
      }
    }

    ${DropdownBody} {
      position: absolute;
      margin-left: ${({ direction }) =>
        direction === NavDirection.ROW
          ? `var(--alignItemsDropdown)`
          : `0`}; // alignment dropdown title and items
      padding-top: ${({ direction }) =>
        direction === NavDirection.ROW
          ? `var(--ariaDropdown)`
          : `0`}; // it is required that the dropdown menu does not disappear for horizontal navbar
      margin-top: 0;
      bottom: ${({ direction }) =>
        direction === NavDirection.ROW ? 'none' : '0'};
      right: ${({ direction }) =>
        direction === NavDirection.ROW ? 'none' : '0'};
      transform: ${({ direction }) =>
        direction === NavDirection.ROW ? 'none' : 'translate(100%)'};

      & ${DropdownItemTypeButtonStyled} {
        padding: 0;

        & ${NavTabLink} {
          padding: var(${FIXED_INDENTS.INDENT_20})
            var(${FIXED_INDENTS.INDENT_40});
        }
      }
    }
  }
`
